/**
 * Newsletter Popup
 * Shows a subscribe popup based on configurable trigger conditions
 * and submits the email directly to Klaviyo's client-side subscribe API.
 */

class NewsletterPopup {
  constructor() {
    this.popup = document.getElementById('NewsletterPopup');
    if (!this.popup) return;

    this.form = document.getElementById('NewsletterPopupForm');
    this.emailInput = document.getElementById('NewsletterPopupEmail');
    this.errorElement = document.getElementById('NewsletterPopupError');
    this.successView = document.getElementById('NewsletterPopupSuccessView');
    this.discountCopyBtn = document.getElementById('NewsletterPopupDiscountCopy');
    this.submitBtn = this.form ? this.form.querySelector('.newsletter-popup__submit') : null;

    this.trigger = this.popup.dataset.trigger || 'delay';
    this.delaySeconds = parseFloat(this.popup.dataset.delaySeconds || '5');
    this.scrollPercentage = parseFloat(this.popup.dataset.scrollPercentage || '50');
    this.storageType = this.popup.dataset.storageType || 'local';
    this.storageKey = 'newsletter_popup_shown';
    this.klaviyoCompanyId = this.popup.dataset.klaviyoCompanyId || '';
    this.klaviyoListId = this.popup.dataset.klaviyoListId || '';
    this.forceShow = this.popup.dataset.forceShow === 'true';

    this.exitIntentTriggered = false;
    this.scrollTriggered = false;

    this.setup();
  }

  setup() {
    if (!this.forceShow && this.hasAlreadyShown()) return;

    this.setupCloseListeners();
    this.setupFormSubmit();
    this.setupTrigger();
  }

  getStorage() {
    return this.storageType === 'session' ? sessionStorage : localStorage;
  }

  hasAlreadyShown() {
    try {
      return this.getStorage().getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  markAsShown() {
    if (this.forceShow) return;

    try {
      this.getStorage().setItem(this.storageKey, 'true');
    } catch (error) {
      // Ignore storage errors (e.g. private browsing)
    }
  }

  setupTrigger() {
    if (this.forceShow) {
      this.openPopup();
      return;
    }

    switch (this.trigger) {
      case 'scroll':
        document.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        break;
      case 'exit_intent':
        document.addEventListener('mouseout', (e) => this.handleExitIntent(e));
        break;
      case 'immediate':
        this.openPopup();
        break;
      case 'delay':
      default:
        setTimeout(() => this.openPopup(), this.delaySeconds * 1000);
        break;
    }
  }

  handleScroll() {
    if (this.scrollTriggered) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

    if (scrolled >= this.scrollPercentage) {
      this.scrollTriggered = true;
      this.openPopup();
    }
  }

  handleExitIntent(event) {
    if (this.exitIntentTriggered) return;

    if (!event.relatedTarget && event.clientY <= 0) {
      this.exitIntentTriggered = true;
      this.openPopup();
    }
  }

  setupCloseListeners() {
    const closeBtn = this.popup.querySelector('.newsletter-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup());
    }

    const overlay = this.popup.querySelector('.newsletter-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.classList.contains('hidden')) {
        this.closePopup();
      }
    });
  }

  setupFormSubmit() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    if (this.discountCopyBtn) {
      this.discountCopyBtn.addEventListener('click', () => this.handleCopyDiscountCode());
    }
  }

  openPopup() {
    if (!this.popup.classList.contains('hidden')) return;

    this.popup.classList.remove('hidden');
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.markAsShown();
  }

  closePopup() {
    this.popup.classList.add('hidden');
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.emailInput.validity.valid) {
      this.showMessage(this.errorElement, this.emailInput.dataset.errorInvalid || 'Please enter a valid email address.');
      return;
    }

    const email = this.emailInput.value.trim().toLowerCase();

    this.setLoading(true);

    try {
      await this.subscribeToKlaviyo(email);
      this.form.reset();
      this.showSuccessView();
      setTimeout(() => this.closePopup(), 3000);
    } catch (error) {
      console.error('Newsletter popup subscription error:', error);
      this.showMessage(this.errorElement, this.errorElement.dataset.errorGeneric || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  showSuccessView() {
    this.errorElement.classList.add('hidden');
    this.form.classList.add('hidden');
    if (this.successView) this.successView.classList.remove('hidden');
  }

  async handleCopyDiscountCode() {
    const code = this.discountCopyBtn.dataset.code || '';
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      this.discountCopyBtn.classList.add('is-copied');
      setTimeout(() => this.discountCopyBtn.classList.remove('is-copied'), 2000);
    } catch (error) {
      console.error('Failed to copy discount code:', error);
    }
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;

    const text = this.submitBtn.querySelector('.newsletter-popup__submit-text');
    const spinner = this.submitBtn.querySelector('.newsletter-popup__submit-spinner');

    this.submitBtn.disabled = isLoading;
    if (text) text.classList.toggle('hidden', isLoading);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  showMessage(element, message) {
    this.errorElement.classList.add('hidden');

    if (element) {
      element.textContent = message;
      element.classList.remove('hidden');
    }
  }

  async subscribeToKlaviyo(email) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompanyId}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.klaviyoListId,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2026-04-15',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 202) {
      return { success: true };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Klaviyo API Error:', errorData);
      throw new Error(`Klaviyo API returned ${response.status}`);
    }

    return { success: true };
  }
}

document.addEventListener('DOMContentLoaded', () => new NewsletterPopup());
